import { IconCheck, IconClose } from '@hulu-react-style-components/icons';
import { TextField as TrekTextField } from '@hulu/react-style-components';
import React, { memo, useCallback, useState } from 'react';

import bem from '../../../../../utils/bem';

const LOWER_LIMIT = -99;
const UPPER_LIMIT = 800;

type Props = Readonly<{
  initialValue: string;
  onSubmit: (value: number) => void;
  onCancel: () => void;
}>;

const [, element] = bem('buffer-cell');

function BufferEditInput({ initialValue, onSubmit, onCancel }: Props): React.JSX.Element {
  const [inputValue, setInputValue] = useState(initialValue);
  const [error, setError] = useState(false);

  const handleSubmit = useCallback((): void => {
    const trimmed = parseFloat(inputValue.replace(/%$/, ''));
    if (Number.isNaN(trimmed) || trimmed < LOWER_LIMIT || trimmed > UPPER_LIMIT) {
      setError(true);
    } else {
      setError(false);
      onSubmit(trimmed);
    }
  }, [inputValue, onSubmit]);

  const handleCancel = useCallback((): void => {
    setError(false);
    onCancel();
  }, [onCancel]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      e.stopPropagation();
      handleSubmit();
    }
    if (e.key === 'Escape') {
      e.stopPropagation();
      handleCancel();
    }
  };

  const onChangeHandler = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setInputValue(e.target.value || '');
  };

  return (
    <div className={element('edit-container')}>
      <TrekTextField
        value={inputValue}
        onChange={onChangeHandler}
        onKeyDown={onKeyDown}
        autoFocus
        type="number"
        placeholder={`${LOWER_LIMIT} - ${UPPER_LIMIT}`}
        isErr={error}
      />
      <div className={element('buttons')}>
        {error && (
          <span className={element('error-msg')}>{`Enter number between ${LOWER_LIMIT} and ${UPPER_LIMIT}`}</span>
        )}
        <button
          className={element('cancel-btn')}
          onClick={(e): void => {
            e.stopPropagation();
            handleCancel();
          }}
          aria-label="cancel button"
        >
          <IconClose title="Cancel Changes" />
        </button>
        <button
          className={element('submit-btn')}
          onClick={(e): void => {
            e.stopPropagation();
            handleSubmit();
          }}
          aria-label="submit button"
        >
          <IconCheck title="Submit Changes" />
        </button>
      </div>
    </div>
  );
}

export default memo(BufferEditInput);
